import { useDroppable } from "@dnd-kit/core";
import { FileDown, Inbox, RefreshCw } from "lucide-react";
import { Button } from "@/shared/components/form/Button";
import type { ColumnId } from "../types/confirmations.types";
import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

type ConfirmationColumnProps = {
  columnId: ColumnId;
  title: string;
  count: number;
  variant: "pending" | "confirmed";
  droppableId?: string;
  isDropTarget?: boolean;
  emptyMessage?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  children: React.ReactNode;
};

const variantConfig = {
  pending: {
    dotClass: "bg-blue-400 dark:bg-blue-500",
  },
  confirmed: {
    dotClass: "bg-green-400 dark:bg-green-500",
  },
} as const;

export const ConfirmationColumn = ({
  title,
  count,
  variant,
  droppableId,
  isDropTarget = false,
  emptyMessage,
  onRefresh,
  isRefreshing = false,
  children,
}: ConfirmationColumnProps) => {
  const { t } = useTranslations("confirmations");
  const config = variantConfig[variant];
  const isConfirmedColumn = variant === "confirmed";

  const { setNodeRef } = useDroppable({
    id: droppableId ?? `column-${variant}`,
    disabled: !droppableId,
  });

  return (
    <div className="flex flex-col space-y-4 flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2 px-2">
        <div className="flex items-center space-x-3">
          <span
            className={cn("w-2.5 h-2.5 rounded-full shrink-0", config.dotClass)}
            aria-hidden
          />
          <h3 className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider text-xs">
            {title}
          </h3>
          <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold">
            {count}
          </span>
        </div>
        {onRefresh && (
          <Button
            type="button"
            variant="outline"
            onClick={onRefresh}
            disabled={isRefreshing}
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isRefreshing && "animate-spin")}
                aria-hidden
              />
            }
            aria-label={t("column_refresh_aria")}
            className="shrink-0"
          >
            {t("column_refresh")}
          </Button>
        )}
      </div>

      <div
        ref={droppableId ? setNodeRef : undefined}
        data-column-id={variant}
        className={cn(
          "flex-1 space-y-3 min-h-[200px] rounded-xl transition-colors",
          isConfirmedColumn && "min-h-[280px]",
          isDropTarget &&
            "bg-green-50 dark:bg-green-500/10 border-2 border-dashed border-green-300 dark:border-green-400"
        )}
      >
        {count === 0 && emptyMessage ? (
          <div className="flex flex-col items-center justify-center min-h-[180px] rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-8 text-center">
            <Inbox className="h-10 w-10 shrink-0 text-slate-300 dark:text-slate-500 mb-3" aria-hidden />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {emptyMessage}
            </p>
          </div>
        ) : (
          children
        )}

        {isConfirmedColumn && (
          <div
            data-drop-zone="confirmed-empty"
            className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl h-32 flex flex-col items-center justify-center text-slate-400 dark:text-slate-600"
          >
            <FileDown className="h-8 w-8 mb-2" />
            <p className="text-xs font-medium">{t("column_drag_to_confirm")}</p>
          </div>
        )}
      </div>
    </div>
  );
};
